// **تذكرةُ بصمة الانصراف عند الخروج** (قرار المالك 22/09/2026: «وهو خارج في آخر اليوم
// تفكّره ببصمة الانصراف»).
//
// ونسيانُ الانصراف أسوأُ من نسيان الحضور: الحضور يُنسى فيُلاحَظ في أوّل اليوم ويُستدرك،
// أمّا الانصراف فيُنسى ويمشي صاحبُه — فيبقى اليوم ناقصًا في السجل، وتُحسب ساعاتُه على غير
// وجهها، ولا يُصلَح إلّا بطلبٍ وموافقة. **فالتذكرة تقع في اللحظة الوحيدة التي تنفع فيها:
// وهو يضغط «خروج» ولسه على الجهاز.**
//
// **ولا تُسأل إلّا إن كان لها معنى**: من ليس في الحضور، أو لم يبصم حضورًا أصلًا، أو بصم
// انصرافه فعلًا — يخرج فورًا بلا نافذة. ولا يُعطَّل الخروج إن تعذّرت القراءة: الخروج حقٌّ
// لا يُحبس بانتظار ردّ خادم.
//
// **ولا تحبس أحدًا**: «اخرج من غير تسجيل» موجودٌ وظاهر. قد يكون خارجًا لغداء، أو على
// جهازٍ ليس جهازه، أو نسي أنه بصم من تليفونه. التذكرةُ تنبيهٌ لا بوّابة.
import { isOpenSession } from './TapButtons.tsx';
import { useCallback, useState } from 'react';
import { get, type AttendanceToday } from '../lib/api.ts';
import { Alert, Modal } from '../components/ui.tsx';
import { cairoTimeAr } from '../lib/format.ts';
import { useTap } from './useTap.ts';

/**
 * يلفّ «خروج» بسؤالٍ عن الانصراف عند الحاجة.
 *
 * @returns `requestSignOut` تُوضع مكان `signOut` في كل زرّ خروج، و`exitDialog` يُرسم
 *          في الشجرة مرّةً واحدة.
 */
export function useExitCheckOut(enrolled: boolean, signOut: () => void | Promise<void>): {
  requestSignOut: () => void;
  exitDialog: React.ReactNode;
} {
  const [asking, setAsking] = useState<AttendanceToday | 'unknown' | null>(null);

  const requestSignOut = useCallback(() => {
    if (!enrolled) { void signOut(); return; }
    void (async () => {
      try {
        const t = await get<AttendanceToday>('/attendance/today');
        // لا سؤالَ إلّا لمن بصم حضورًا ولم يبصم انصرافًا — وما عدا ذلك خروجٌ فوري
        if (isOpenSession(t)) { setAsking(t); return; }
      } catch {
        /**
         * تعذّرت القراءة: **يُسأل ولا يُسكَت عنه** (قرار المالك 22/09/2026: «وهو هيقفل
         * فكّره ببصمة الانصراف»). فالصمتُ هنا يعني يومًا يُقفل بلا انصراف ولا تنبيه،
         * وتصليحُه بعدها طلبٌ وموافقة. والسؤالُ يُغلق بضغطة.
         */
        setAsking('unknown');
        return;
      }
      void signOut();
    })();
  }, [enrolled, signOut]);

  const exitDialog = asking
    ? <ExitDialog today={asking === 'unknown' ? null : asking}
        onLeave={() => { setAsking(null); void signOut(); }}
        onStay={() => setAsking(null)} />
    : null;

  return { requestSignOut, exitDialog };
}

function ExitDialog({ today, onLeave, onStay }: {
  /** `null` = تعذّرت قراءة حالة اليوم — يُذكَّر بلا ادّعاء وقتٍ لم يُقرأ */
  today: AttendanceToday | null; onLeave: () => void; onStay: () => void;
}) {
  // بعد تسجيل الانصراف لا يبقى ما يُنتظر: يُقال الوقت ثم يُخرَج
  const { busy, error, done, tap } = useTap(async () => { /* الخروج بيد صاحبه بعد أن يقرأ الوقت */ });

  return (
    <Modal size="sm" title="ثانية قبل ما تمشي" onClose={() => { if (busy === null) onStay(); }}>
      {done ? (
        <>
          <p className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm font-semibold text-emerald-900">
            ✅ اتسجّل انصرافك الساعة {cairoTimeAr(done.at)} — تسلم إيدك، وشوفك بكره.
          </p>
          <div className="mt-4">
            <button type="button" className="btn-primary px-5 py-2" onClick={onLeave}>تسجيل الخروج</button>
          </div>
        </>
      ) : (
        <>
          <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-3 text-amber-950">
            <div className="text-sm font-bold">⏰ ماتنساش بصمة الانصراف</div>
            <p className="mt-1 text-xs leading-relaxed">
              {today?.firstCheckInAt
                ? `حضورك متسجّل من الساعة ${cairoTimeAr(today.firstCheckInAt)}، وانصرافك لسه.`
                : 'قبل ما تمشي، اتأكّد إنك سجّلت انصرافك.'}
              {' '}اليوم اللي بيقفل من غير انصراف بيتحسب ناقص — وتصليحه محتاج طلب وموافقة.
            </p>
          </div>
          {error && <div className="mt-3"><Alert>{error}</Alert></div>}
          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" className="btn-primary px-5 py-2" disabled={busy !== null}
              onClick={() => void tap('CHECK_OUT')}>
              {busy !== null ? 'جارٍ التسجيل…' : 'سجّل انصرافي'}
            </button>
            <button type="button" className="btn-ghost px-5 py-2" disabled={busy !== null} onClick={onLeave}>
              اخرج من غير تسجيل
            </button>
            <button type="button" className="btn-ghost px-5 py-2" disabled={busy !== null} onClick={onStay}>
              لسه بشتغل
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}
